// Артефакты — то, что пользователь уносит с собой.
// Источник: design/README.md, экраны 06, 07, 08.

import { truncate } from './text.ts'
import type { EditPolicy } from './units.ts'

/**
 * Типы артефактов.
 *
 * ⚠️ `search` убран 06.09.2026 вместе с одноимённым сценарием. Веб-поиск
 * остался, но результатом его стал обычный ответ в ленте, а не карточка,
 * которую уносят с собой: ссылки живут у запуска (`run_sources`), вывод — в
 * тексте ответа. Артефакт — это то, что открывают из истории отдельной
 * строкой и выгружают файлом; ответ в разговоре ни тем, ни другим не был.
 *
 * Оставленное значение было бы «третьим состоянием» — тип есть, карточки под
 * ним нет, — поэтому 0026 удаляет его из перечисления. Разбор — в
 * scenarios.ts.
 */
export const ARTIFACT_TYPES = ['doc_review', 'doc_create', 'image'] as const
export type ArtifactType = (typeof ARTIFACT_TYPES)[number]

/** Уровень риска пункта на экране 07. Служебные цвета, не бренд-цвет. */
export const RISK_LEVELS = ['high', 'medium', 'low'] as const
export type RiskLevel = (typeof RISK_LEVELS)[number]

/** Строка блока «Рискованные пункты · N». */
export interface DocRisk {
  /** Чем рискует пользователь, его словами: «Расторжение в любой момент». */
  title: string
  /**
   * Номер пункта документа: «4.2».
   *
   * Отдельным полем, а не внутри заголовка: экран 07 показывает его мелким
   * шрифтом рядом с уровнем, а дисклеймер называет пункт, где нужен юрист.
   * Пусто — привязать к пункту не вышло; выдумывать номер нельзя, по нему
   * человек пойдёт в документ.
   */
  clause?: string
  level: RiskLevel
}

/**
 * Результат сценария «Разобрать документ» — экран 07. SMY-50 · P4 Э4.2
 *
 * Сценарий с наибольшей воспринимаемой ценностью: сквозной пример продукта
 * построен именно на нём. Пропуски здесь такая же часть результата, как
 * риски, — условие, которого в договоре НЕТ, человек сам не заметит.
 */
export interface DocReview {
  /**
   * Плашка файла: «Договор аренды.pdf / 14 страниц · роль: арендатор».
   *
   * `truncated` означает, что документ разобран не целиком. Тогда пустой
   * список пропусков читается как «не смотрели», а не «всё на месте», и
   * интерфейс обязан это различать.
   */
  file: { name: string; pages?: number; note?: string; truncated?: boolean }
  /** Блок «Кратко» — резюме на одной странице. */
  summary: string
  risks: DocRisk[]
  /** Блок «Пропущено · N»: условия, которых в документе нет. */
  missing: string[]
  /** Вопросы второй стороне — то, что стоит спросить до подписи. */
  questions: string[]
  /** Предложенные поправки. Пользователь их правит и уносит в DOCX. */
  amendments: DocAmendment[]
  disclaimer: string
}

export interface DocAmendment {
  /** К какому пункту поправка. Пусто — новое условие, которого не было. */
  clause?: string
  text: string
}

/** Что умеет собирать «Создать документ». Из карточки экрана 02. */
export const DOC_TEMPLATES = ['claim', 'application', 'contract', 'plan'] as const
export type DocTemplate = (typeof DOC_TEMPLATES)[number]

export const DOC_TEMPLATE_LABELS: Record<DocTemplate, string> = {
  claim: 'Претензия',
  application: 'Заявление',
  contract: 'Договор',
  plan: 'План',
}

/** Пункт документа. Нумерация не у всех типов: у плана её обычно нет. */
export interface DocClause {
  number?: string
  text: string
}

/**
 * Результат сценария «Создать документ». SMY-51 · P4 Э4.2
 *
 * Отдельного экрана в дизайне у него нет — результат живёт в ленте карточкой
 * со ссылкой на файл. Поэтому пункты лежат здесь целиком: по ним строится и
 * предпросмотр, и повторный экспорт после правки.
 */
export interface DocDraft {
  template: DocTemplate
  title: string
  clauses: DocClause[]
  /**
   * Что пользователь обязан подставить сам: даты, суммы, имена.
   *
   * Не украшение и не подсказка: единица обещает шаблон, а не готовый
   * документ, и список скобок — это и есть граница между ними.
   */
  placeholders: string[]
  /**
   * Существенные условия, которых в документе нет.
   *
   * Проверка полноты обязана быть видна: документ с пропущенным предметом или
   * сроком выглядит готовым и не работает.
   */
  missing: string[]
  /**
   * Состоялась ли проверка полноты.
   *
   * Без этого поля пустой `missing` читается как «проверили, всё на месте» —
   * а он бывает пустым и тогда, когда проверять оказалось некому: второго
   * поставщика может не быть вовсе. Выдавать непроверенное за проверенное
   * нельзя, поэтому у молчания есть отдельное значение.
   */
  checked: boolean
  disclaimer: string
}

/**
 * Быстрые правки картинки — чипы экрана 08, входят в списание.
 *
 * Список фиксированный и короткий: это не свободный запрос, а обещание
 * «правка входит в то, что уже списано». Свободная переделка — новая
 * генерация, и она объявляет цену (SMY-52, SMY-54).
 */
export const QUICK_EDITS = ['Фон светлее', 'Убрать тень', 'Ближе кадр'] as const

/**
 * Результат сценария «Изображение» — экран 08. SMY-52 · P4 Э4.3
 *
 * Самих картинок здесь нет: они лежат файлами, а в `fileIds` — ссылки на них
 * по порядку вариантов. Картинка в jsonb это копия, которая разойдётся с
 * оригиналом при первой правке, и вдобавок база вместо хранилища.
 *
 * Поля «какой вариант выбран» нет намеренно: вариант один, и выбирать не из
 * чего. Оно вернётся вместе со вторым вариантом, а не будет лежать нулём.
 *
 * Сколько вариантов рисовать — не здесь: с SMY-57 это `units.scope.images` в
 * конфигурации, и меняется оно из админки, а не константой в коде.
 */
export interface ImageVariants {
  /** Запрос, по которому рисовали. Экран 08 показывает его плашкой. */
  prompt: string
  /**
   * Исходный запрос человека — без правок. SMY-186
   *
   * Отдельно от `prompt`, потому что перерисовка собирает запрос заново
   * (`redrawPrompt`): что рисуем — отсюда, чем поправили — из `edits`. В одной
   * склеенной строке их не различить: запятые есть и в самом запросе.
   * Пусто у карточек до 21.09.2026 — тогда исходным считается `prompt`.
   */
  base?: string
  /** Правки, с которыми нарисована картинка, от старой к новой. */
  edits?: string[]
  /** Чипы быстрой правки. */
  quickEdits: string[]
  /** 243-ФЗ: та же пометка, что вписана внутрь самих файлов. */
  notice: string
  /** Файлы вариантов по порядку. Проставляет движок после сохранения. */
  fileIds?: string[]
}

/**
 * Сколько знаков запроса картинка учитывает надёжно — по замеру, а не по
 * документации. SMY-186
 *
 * Документация ART 3.0 называет 500 символов, но API их не применяет. Замер
 * 21.09.2026 — шесть генераций на сервере, тем же адаптером: деталь с 524-го и
 * с 1014-го знака нарисована на всех трёх картинках, а в запросе на 1950 знаков
 * деталь с 1889-го выпала. Граница где-то между 1075 и 1889 знаками, и тысяча —
 * с запасом ниже проверенного. Разбор — `docs/models.md`, строка ART.
 *
 * Это не запрет: длинный запрос уходит как есть, а человек видит
 * предупреждение (`imagePromptNote`). Держит число перерисовка — её запрос
 * собирает сервер, и там его можно не раздувать (`redrawPrompt`).
 */
export const IMAGE_PROMPT_MAX_CHARS = 1000

/**
 * Сколько начала исходного запроса остаётся, даже когда правка длинная.
 * Без предмета («что рисуем») перерисовка рисует не то, а не «то же, но в
 * шляпе».
 */
const REDRAW_MIN_BASE = 200

/**
 * Запрос перерисовки: исходный запрос, правки и новая просьба. SMY-186
 *
 * ART не правит картинку, а рисует заново по тексту, поэтому каждая правка —
 * это запрос целиком. До 21.09.2026 он рос склейкой `прежний, новая`, и новая
 * просьба вставала в самый конец — туда, что за границей запроса теряется
 * первым. После нескольких правок «добавь шляпу» молча не доходило до картинки,
 * а правка засчитывалась.
 *
 * Теперь запрос держится в `IMAGE_PROMPT_MAX_CHARS`, и порядок уступок такой:
 * 1. новая просьба — всегда целиком, ради неё и перерисовываем;
 * 2. первыми уходят самые старые правки;
 * 3. исходный запрос режется последним и с конца: начало — это то, что рисуем.
 *
 * Если длинна одна новая просьба, от исходного запроса остаются первые
 * `REDRAW_MIN_BASE` знаков, и запрос выходит длиннее предела — предупреждение
 * человек видит у поля ввода. `base` отдаётся целым: следующая перерисовка
 * режет его заново под свои правки.
 */
export function redrawPrompt(
  previous: Pick<ImageVariants, 'prompt' | 'base' | 'edits'>,
  edit: string,
  max = IMAGE_PROMPT_MAX_CHARS,
): { prompt: string; base: string; edits: string[] } {
  const base = previous.base ?? previous.prompt
  const edits = [...(previous.edits ?? []), edit]
  const join = (head: string): string => [head, ...edits].join(', ')

  while (edits.length > 1 && join(base).length > max) edits.shift()
  if (join(base).length <= max) return { prompt: join(base), base, edits }

  const room = Math.max(REDRAW_MIN_BASE, max - join('').length)
  return { prompt: join(cutAtWord(base, room)), base, edits }
}

/** Обрезать по последнему пробелу в пределе — не посреди слова. */
function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text
  const head = truncate(text, max)
  const space = head.lastIndexOf(' ')
  return (space > max / 2 ? head.slice(0, space) : head).replace(/[\s,.;:—-]+$/u, '')
}

/**
 * Что сказать у поля ввода о длине запроса картинки. SMY-186
 *
 * Предупреждение, а не запрет: предел не аварийный, а по замеру — запрос
 * длиннее уходит и рисуется, только конец его может не попасть в картинку.
 * Молчит, пока до предела далеко: счётчик на каждом коротком запросе стал бы
 * фоном, который не читают.
 */
export function imagePromptNote(length: number, max = IMAGE_PROMPT_MAX_CHARS): string | null {
  if (length <= Math.floor(max * 0.8)) return null
  if (length <= max) return `${length} из ${max} знаков — длиннее картинка может не учесть конец запроса.`
  return `В запросе ${length} знаков: картинка надёжно учитывает около ${max}, конец может не попасть в рисунок. Главное лучше сказать в начале.`
}

/**
 * Снимает разметку, которую модель ставит по привычке.
 *
 * Утверждение уезжает не только на экран: оно попадает в PDF, в DOCX и в
 * историю. Звёздочки, оставленные здесь, вылезут во всех четырёх местах, и
 * чинить их в вёрстке значило бы чинить четырежды. Модель просят отвечать
 * JSON'ом, но `**жирный**` внутри строки она ставит и в JSON — замечено на
 * живом прогоне 05.09.2026.
 *
 * Снимаем только парные маркеры и маркер списка в начале. Одиночную звёздочку
 * не трогаем: в тексте она бывает умножением и сноской, и «почистить» её
 * значит испортить смысл. Пустой маркер (`****`) снимается тоже — внутри него
 * ничего нет, и на экране это просто мусор.
 *
 * Живёт в ядре, потому что читателей у правила двое: разбор ответа на сервере
 * и заголовок результата в истории (`artifactTitle`). Второе правило рядом с
 * первым разошлось бы с ним — и первая же попытка это доказала: заголовок
 * пытался чистить `#` и одиночную `*`, то есть ровно то, что здесь оставлено
 * намеренно.
 */
export const plainText = (s: string): string =>
  s
    .replace(/\*\*(.*?)\*\*/gs, '$1')
    .replace(/__(.*?)__/gs, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s*[-*\u2022]\s+/, '')
    .trim()

/**
 * Как результат называется в списке. Экран 09, таблица истории.
 *
 * Заголовок ВЫВОДИТСЯ из нагрузки, а не хранится отдельным полем, и это
 * решение. Отдельное поле пришлось бы заполнять каждому сценарию, и первый же
 * забывший оставил бы в истории строку без имени; хуже — оно разошлось бы с
 * содержимым после бесплатной правки, которая меняет как раз текст.
 *
 * Ничего не выдумывается: у разбора это имя файла, у картинки — запрос,
 * которым её рисовали. Не нашлось ничего — пусто, и строку назовёт сценарий;
 * выдуманное имя в истории хуже честного «Документ».
 */
export function artifactTitle(type: ArtifactType, payload: unknown): string {
  const p = (payload ?? {}) as Record<string, unknown>

  if (type === 'doc_review') {
    const file = p['file'] as DocReview['file'] | undefined
    // Расширение убирается: «Договор аренды.pdf» в списке читается как файл,
    // а строка истории — про разбор, а не про файл.
    const name = (file?.name ?? '').replace(/\.[a-zA-Z0-9]{1,5}$/, '')
    return name === '' ? '' : `${name} — разбор рисков`
  }
  if (type === 'doc_create') return first(p['title'])
  if (type === 'image') return first(p['prompt'])
  return ''
}

/**
 * Первая фраза, очищенная и обрезанная до длины строки списка.
 *
 * Разметку снимает `plainText` — то же правило, что и на разборе ответа. Своё
 * здесь было бы вторым и, как выяснилось, более грубым: оно резало одиночную
 * звёздочку, которую в тексте оставляют намеренно.
 *
 * Первая буква поднимается в заглавную: утверждение начинали не с начала
 * предложения, а строка списка начинается.
 */
function first(value: unknown): string {
  if (typeof value !== 'string') return ''
  const plain = plainText(value).replace(/\s+/g, ' ').trim()
  let text = plain.split(/(?<=[.!?])\s/)[0]?.trim() ?? ''
  if (text.length > 70) {
    // По границе слова, а не по букве: обрыв посреди слова читается как сбой.
    text = `${truncate(text, 70).replace(/\s+\S*$/, '')}…`
  }
  return text === '' ? '' : text.charAt(0).toUpperCase() + text.slice(1)
}

export interface Artifact {
  id: string
  type: ArtifactType
  payload: unknown
  /**
   * Файл результата: экспорт документа, сгенерированное изображение.
   *
   * Ссылкой, а не содержимым: файл живёт в хранилище со своим сроком и своей
   * выдачей по подписанной ссылке, а артефакт — в базе. Класть DOCX в jsonb
   * значит завести вторую копию, которая разойдётся с первой.
   */
  fileId?: string
  /**
   * Когда закроется окно бесплатной правки. Пусто — окна нет вовсе, и правки
   * считаются числом, а не часами (см. `EditPolicy`).
   */
  editWindowExpiresAt?: string
}

/**
 * Правка бесплатна: правки не кончились и окно ещё не закрылось.
 *
 * Время сервера передаётся явно: часы на устройстве доверия не заслуживают, а
 * на этом решении держатся деньги.
 *
 * Политика без `minutes` — это осознанное «без окна»: у документа считается
 * число правок, и закрывать его по часам значило бы наказывать за то, что
 * человек внимательно читал.
 */
export function isEditFree(
  artifact: Pick<Artifact, 'editWindowExpiresAt'>,
  serverNow: Date,
  policy: EditPolicy,
  used = 0,
): boolean {
  if (used >= policy.count) return false
  if (policy.minutes == null) return true
  if (artifact.editWindowExpiresAt == null) return false
  return serverNow.getTime() < Date.parse(artifact.editWindowExpiresAt)
}

/**
 * Сколько бесплатных правок результата осталось на самом деле. SMY-336
 *
 * Правки и уточнения делят один запас после оплаченного результата — п. 6.4
 * правил списания единиц. У карточки свой счёт (`policy.count`), у чата —
 * общий остаток (`freeFollowUpsLeft` с сервера), и бесплатна правка, пока есть
 * и то и другое. Ручка правки спрашивает оба (`routes/artifacts.ts`), а
 * клиентам нужно одно число: подсказка, посчитанная по одной карточке,
 * обещала бы правку, которую сервер назовёт новой задачей.
 *
 * Ноль — правка не бесплатна: кончились правки карточки, закрылось окно или
 * вышел общий запас.
 */
export function freeEditsLeft(
  artifact: Pick<Artifact, 'editWindowExpiresAt'>,
  serverNow: Date,
  policy: EditPolicy,
  used: number,
  followUpsLeft: number,
): number {
  if (!isEditFree(artifact, serverNow, policy, used)) return 0
  return Math.max(0, Math.min(policy.count - used, followUpsLeft))
}

/** Что обещает экран о следующей отправке из поля (`nextSendCost`). */
export type NextSendCost = 'free_edit' | 'free_followup' | 'charged'

/**
 * Чем станет следующая отправка из поля — одно правило для строки цены под
 * полем и для блока допущений (SMY-497, В4-Б; ворота 27.10.2026,
 * dedup-hunter и test-auditor): до него условие жило тернарником в каждом
 * экране, а блок повторял его своей функцией, и правка одного места развела бы
 * «спишется» в блоке с «без списания» под полем.
 *
 * Бесплатная правка результата сильнее уточнений: окно короче и относится к
 * тому, что человек видит сейчас. `freeEdit` — экран обещает правку
 * (`freeEditsLeft` > 0 и карточек уточнений нет), `freeFollowUpsLeft` —
 * общий запас чата. Списывает всё равно сервер; это только то, что экран
 * обещает.
 */
export function nextSendCost(freeEdit: boolean, freeFollowUpsLeft: number): NextSendCost {
  if (freeEdit) return 'free_edit'
  return freeFollowUpsLeft > 0 ? 'free_followup' : 'charged'
}

/**
 * Выдана ли карточка не раньше последнего оплаченного запуска чата. SMY-337
 *
 * Общий запас принадлежит последнему оплаченному результату: «обращения к
 * результату», п. 6.4 правил. Карточка, выданная раньше него, своих бесплатных
 * обращений уже не имеет, и сервер отвечает на её правку «новая задача»
 * (`freeFollowUpsLeft` с порядком запуска карточки). Без этого дешёвый
 * оплаченный вопрос пополнял запас, и перерисовки последней картинки шли
 * дальше бесплатно.
 *
 * `runs` — запуски чата по порядку заведения: так их отдаёт лента.
 * Запуска карточки в ленте нет — считаем, что не выдана: обещать бесплатную
 * правку по неизвестному нельзя.
 */
export function isCurrentResult(
  runs: readonly { runId: string; charged: boolean }[],
  runId: string,
): boolean {
  const at = runs.findIndex((r) => r.runId === runId)
  if (at < 0) return false
  return at >= lastChargedIndex(runs)
}

/**
 * Где в ленте последний оплаченный запуск; −1 — оплаченных нет. SMY-497
 *
 * Одно правило «текущего результата» на запас бесплатных обращений
 * (`isCurrentResult`) и на место блока допущений (`assumptionsRunId`): разойдись
 * они, блок «Изменить» встал бы под ответом, правку которого сервер счёл бы
 * новой задачей. Циклом, а не `findLastIndex`: движок приложения знает его не
 * во всех сборках.
 */
export function lastChargedIndex(runs: readonly { charged: boolean }[]): number {
  for (let i = runs.length - 1; i >= 0; i--) if (runs[i]?.charged === true) return i
  return -1
}

/**
 * Когда закроется окно правки. `null` — окна нет вовсе.
 *
 * Считается от политики сценария, а не от общего числа: минуты названы
 * пользователю строкой под полем ввода, и разойтись этим двум местам нельзя —
 * расхождение здесь выглядит как обман, а не как опечатка.
 */
export const editWindowExpiry = (issuedAt: Date, policy: EditPolicy): string | null =>
  policy.minutes == null ? null : new Date(issuedAt.getTime() + policy.minutes * 60_000).toISOString()

/**
 * Правка входит в исходное списание, новая задача списывается заново, а
 * вопрос не делает ни того, ни другого: он уходит бесплатным уточнением в чат.
 */
export type FollowUp = 'free_edit' | 'new_task' | 'question'

/**
 * Слова, которыми просят НОВОЕ, а не поправленное. SMY-54
 *
 * Граница проходит по существу запроса, а не по его длине: «сделай вывод
 * короче» — правка, «а проверь ещё и вот это утверждение» — новая задача, и
 * второе длиннее первого всего на несколько слов. Общее у новых задач одно —
 * в них появляется другой предмет, и по-русски он почти всегда объявляется
 * одним из этих слов.
 *
 * Список намеренно узкий. Ошибиться в сторону правки значит подарить один
 * дешёвый прогон; ошибиться в сторону новой задачи значит списать единицу за
 * то, что человек считает исправлением, — а это уже та самая непрозрачность
 * списания, против которой продукт и сделан.
 */
export const NEW_TASK_MARKERS = [
  'ещё раз',
  'ещё и',
  'а ещё',
  'заново',
  'с нуля',
  'вместо',
  'другой',
  'другую',
  'другое',
  'другие',
  'а теперь',
  'а также проверь',
  'проверь ещё',
  'найди ещё',
  'сделай новый',
  'сделай новую',
  'сделай новое',
] as const

/** Просят ли новое, а не поправленное. Регистр и «ё» значения не имеют. */
export function looksLikeNewTask(text: string): boolean {
  const normalized = normalize(text)
  return NEW_TASK_MARKERS.some((m) => normalized.includes(marker(m)))
}

const normalize = (text: string): string => text.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()

/**
 * Маркер к сравнению. Только «ё», без `trim`: у коротких слов пробел на конце
 * несёт границу слова — без него «что » нашлось бы в «чтобы».
 */
const marker = (m: string): string => m.replace(/ё/g, 'е')

/**
 * Слова, которыми СПРАШИВАЮТ о результате, а не просят его переписать.
 *
 * Третий случай появился не из красоты классификации, а из живого разбора
 * договора (08.09.2026). Человек получил разбор и дописал «какие тут могут
 * быть риски для заказчика?» — вопрос ушёл в правку, потому что правкой
 * считалось всё, в чём нет слов новой задачи. У `doc_review` правится только
 * `summary`, вопрос ничего в нём не меняет, и обратно приехала та же карточка:
 * бесплатная правка израсходована, ответа нет.
 *
 * Отсюда правило: **вопрос — это не правка результата, а следующее сообщение
 * разговора.** Ровно так и устроены бесплатные уточнения (FREE_FOLLOWUPS), и
 * `freeEdits.chat` в конфигурации это прямо проговаривает: «ответ в чате
 * правят не правкой артефакта, а следующим сообщением».
 *
 * Список привязан к НАЧАЛУ запроса, а не ищется по всей строке. Иначе
 * «перепиши так, чтобы было понятно, что это черновик» стало бы вопросом из-за
 * «что» в середине. Вежливая просьба с вопросительным знаком — «можешь
 * сделать короче?» — остаётся правкой: она начинается не с вопросительного
 * слова, и знак здесь про вежливость, а не про вопрос.
 */
export const QUESTION_MARKERS = [
  'что ',
  'чем ',
  'чего ',
  'какой',
  'какая',
  'какое',
  'какие',
  'каких',
  'каким',
  'сколько',
  'почему',
  'зачем',
  'когда',
  'куда',
  'где ',
  'кто ',
  'кому',
  'кем ',
  'можно ли',
  'нужно ли',
  'стоит ли',
  'правда ли',
  'значит ли',
  'есть ли',
  'а если',
  'объясни',
  'поясни',
  'расскажи',
  'подскажи',
] as const

/**
 * Спрашивают ли о результате, а не правят его.
 *
 * Ошибиться сюда дешевле, чем из неё: принятый за вопрос «сделай короче»
 * ответит текстом вместо новой карточки — обидно, но человек видит ответ и
 * повторяет просьбу. Принятый за правку вопрос не отвечает вовсе и молча
 * тратит бесплатную правку — ровно то, из-за чего эта функция и написана.
 */
export function looksLikeQuestion(text: string): boolean {
  const normalized = normalize(text)
  return QUESTION_MARKERS.some((m) => {
    const found = marker(m)
    if (!normalized.startsWith(found)) return false
    // Дефис за вопросительным словом делает его неопределённым местоимением:
    // «какие-то пункты убери» — это правка, а не вопрос. Пробел на конце
    // коротких маркеров такое ловит сам, у длинных ловить нечем.
    return normalized[found.length] !== '-'
  })
}

/**
 * Правка это или новая задача.
 *
 * Порядок правил значим и идёт от самого твёрдого основания к самому мягкому.
 * Сначала факты: правки кончились или окно истекло. Потом прочтение запроса —
 * только оно может ошибиться, поэтому оно последнее и по умолчанию трактует
 * запрос в пользу пользователя.
 */
export function followUpFor(args: {
  text: string
  artifact: Pick<Artifact, 'editWindowExpiresAt'>
  /** Сколько бесплатных правок этого результата уже израсходовано. */
  editsUsed: number
  /** Сколько их полагается и есть ли окно. Своё у каждого сценария. */
  policy: EditPolicy
  serverNow: Date
}): FollowUp {
  // Вопрос проверяется ПЕРВЫМ и мимо фактов о правках: спросили о результате —
  // значит, не правят его, и остаток бесплатных правок к этому отношения не
  // имеет. Ответит разговор, а он живёт по своему счёту (FREE_FOLLOWUPS).
  if (looksLikeQuestion(args.text)) return 'question'
  if (!isEditFree(args.artifact, args.serverNow, args.policy, args.editsUsed)) return 'new_task'
  return looksLikeNewTask(args.text) ? 'new_task' : 'free_edit'
}
